import type { GameFlow, GameFlowSnapshot } from "../application/game-flow";

export interface InactiveRenderLoop {
  readonly running: boolean;
  resetDelta(): void;
  sleep(): void;
  wake(): void;
}

export interface InactiveRenderScene {
  prepareForRenderWake(): void;
  setRenderActive(active: boolean): void;
}

export interface InactiveRenderViewport {
  readonly refresh: () => void;
  readonly source: {
    addEventListener(type: "resize", listener: () => void): void;
    removeEventListener(type: "resize", listener: () => void): void;
  };
}

export type RenderLifecycleDeferrer = (task: () => void) => void;

const deferWithMicrotask: RenderLifecycleDeferrer = (task) => {
  queueMicrotask(task);
};

export class InactiveRenderLifecycle {
  private active: boolean | undefined;
  private destroyed = false;
  private ready = false;
  private snapshot: GameFlowSnapshot;
  private readonly unsubscribe: () => void;

  public constructor(
    flow: GameFlow,
    private readonly loop: InactiveRenderLoop,
    private readonly scene: InactiveRenderScene,
    defer: RenderLifecycleDeferrer = deferWithMicrotask,
    private readonly viewport?: InactiveRenderViewport,
  ) {
    this.snapshot = flow.getSnapshot();
    this.viewport?.source.addEventListener("resize", this.handleResize);
    this.unsubscribe = flow.subscribe((snapshot) => {
      this.snapshot = snapshot;
      if (this.ready) {
        this.synchronize();
      }
    });
    defer(() => {
      if (this.destroyed) {
        return;
      }
      this.ready = true;
      this.synchronize();
    });
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }
    this.destroyed = true;
    this.unsubscribe();
    this.viewport?.source.removeEventListener("resize", this.handleResize);
  }

  private readonly handleResize = (): void => {
    if (this.active === false) {
      this.viewport?.refresh();
    }
  };

  private synchronize(): void {
    const active = this.snapshot.scene === "gameplay";
    if (active === this.active) {
      return;
    }
    this.active = active;
    this.scene.setRenderActive(active);
    if (!active) {
      this.loop.sleep();
      return;
    }
    this.loop.resetDelta();
    this.scene.prepareForRenderWake();
    if (!this.loop.running) {
      this.loop.wake();
    }
  }
}
